import {Accordion, Badge, Group, Stack, Text, ThemeIcon} from "@mantine/core";
import {IconBooks} from "@tabler/icons-react";
import {useMemo} from "react";
import type {LocalLibraryEntry} from "@/app/storage";
import {compareAlphanumeric} from "@/app/utils/stringUtils";
import {LibraryGroupContent} from "./LibraryGroupContent";

interface GroupedData {
  vocab: LocalLibraryEntry[];
  character: LocalLibraryEntry[];
}

export function LibraryList(props: {
  entries: LocalLibraryEntry[];
  onEdit: (entry: LocalLibraryEntry) => void;
  onDelete: (uri: string) => void;
  loading?: boolean;
}) {
  const {entries, onEdit, onDelete, loading} = props;

  const groupedEntries = useMemo(() => {
    const groups: Record<string, GroupedData> = {};
    const noGroup: GroupedData = {vocab: [], character: []};

    for (const entry of entries || []) {
      const {grouping, type} = entry;
      if (grouping && !groups[grouping]) {
        groups[grouping] = {vocab: [], character: []};
      }
      const target = (grouping && groups[grouping]) || noGroup;
      (target[type] || []).push(entry);
    }

    return {groups, noGroup};
  }, [entries]);

  if (loading) return <Text>Loading entries...</Text>;

  if (!entries || entries.length === 0)
    return (
      <Stack align="center" gap="xs" py="xl" ta="center">
        <IconBooks size={40} stroke={1.5} color="var(--mantine-color-dimmed)" />
        <Text fw={500}>No entries found in your library.</Text>
        <Text size="sm" c="dimmed">
          Start by adding some Japanese vocabulary or kanji.
        </Text>
      </Stack>
    );

  return (
    <Accordion variant="separated" radius="md">
      {Object.entries(groupedEntries.groups)
        .sort((a, b) => compareAlphanumeric(a[0], b[0]))
        .map(([groupName, groupData]) => (
          <Accordion.Item key={groupName} value={groupName} className="glass-card">
            <Accordion.Control
              icon={
                <ThemeIcon variant="light" color="teal" radius="xl">
                  <IconBooks size={16} />
                </ThemeIcon>
              }
            >
              <Group justify="space-between" pr="md">
                <Text fw={600}>{groupName}</Text>
                <Badge variant="dot">
                  {groupData.vocab.length + groupData.character.length} items
                </Badge>
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              <LibraryGroupContent
                character={groupData.character}
                vocab={groupData.vocab}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            </Accordion.Panel>
          </Accordion.Item>
        ))}

      {(groupedEntries.noGroup.vocab.length > 0 ||
        groupedEntries.noGroup.character.length > 0) && (
        <Accordion.Item value="ungrouped" className="glass-card">
          <Accordion.Control>
            <Group justify="space-between" pr="md">
              <Text fw={600} c="dimmed">
                Ungrouped Items
              </Text>
              <Badge variant="dot" color="gray">
                {groupedEntries.noGroup.vocab.length +
                  groupedEntries.noGroup.character.length}{" "}
                items
              </Badge>
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            <LibraryGroupContent
              character={groupedEntries.noGroup.character}
              vocab={groupedEntries.noGroup.vocab}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          </Accordion.Panel>
        </Accordion.Item>
      )}
    </Accordion>
  );
}
